import React from 'react';
import styled, { keyframes } from 'styled-components';
import { COLORS } from '../design-system/settings/variables/Colors';
import { isBbUserView, isCnUserView } from '../utils/partnerUser';

const bbSpin = keyframes`
  to { transform: rotate(360deg); }
`;

const BbSpinner = styled.div<{ marginTop: number; marginBottom: number }>`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: conic-gradient(
    rgba(0, 0, 0, 0.04) 0%,
    rgba(0, 0, 0, 0.12) 25%,
    rgba(0, 0, 0, 0.35) 60%,
    rgba(0, 0, 0, 0.65) 100%
  );
  -webkit-mask: radial-gradient(farthest-side, transparent calc(100% - 3px), #fff calc(100% - 3px));
  mask: radial-gradient(farthest-side, transparent calc(100% - 3px), #fff calc(100% - 3px));
  animation: ${bbSpin} 0.9s linear infinite;
  margin: ${({ marginTop }) => marginTop}px auto ${({ marginBottom }) => marginBottom}px;
`;

// CN Theme color palette - shades of CN blue
const cnAnimation = keyframes`
  0%   { background-color: #6687FF; }
  20%  { background-color: #99AAFF; }
  40%  { background-color: #CCD4FF; }
  60%  { background-color: #2A4CCC; }
  80%  { background-color: #213DA5; }
  100% { background-color: #192E80; }
`;

// Original TI theme colors
const tiAnimation = keyframes`
  0%   { background-color: ${COLORS.brandPrimary}; }
  20%  { background-color: ${COLORS.brandPrimarySubdued}; }
  40%  { background-color: ${COLORS.brandPrimaryBackground}; }
  60%  { background-color: ${COLORS.brandSecondaryBackground}; }
  80%  { background-color: ${COLORS.brandSecondarySubdued}; }
  100% { background-color: ${COLORS.brandSecondary}; }
`;

interface StyledProps {
  marginTop: number;
  marginBottom: number;
  paddingTop?: number;
  paddingBottom?: number;
  isCNTheme: boolean;
  isVisible: boolean;
}

const StyledDiv = styled.div<StyledProps>`
  display: grid;
  gap: 8px;
  justify-content: center;
  grid-template-columns: 10px 10px 10px;
  margin-top: ${({ marginTop }) => marginTop}px;
  margin-bottom: ${({ marginBottom }) => marginBottom}px;
  opacity: ${({ isVisible }) => (isVisible ? 1 : 0)};
  transition: opacity 0.1s ease-in;
  ${({ paddingTop }) => paddingTop && `padding-top: ${paddingTop}px;`}
  ${({ paddingBottom }) =>
    paddingBottom && `padding-bottom: ${paddingBottom}px;`}
  div:nth-child(2) {
    animation-delay: 0.4s;
  }
  div:nth-child(3) {
    animation-delay: 0.8s;
  }
  div:nth-child(4) {
    animation-delay: 1.2s;
  }
  div:nth-child(5) {
    animation-delay: 1.6s;
  }
  div:nth-child(6) {
    animation-delay: 2s;
  }
  .dot {
    width: 10px;
    height: 10px;
    background-color: ${({ isCNTheme }) => (isCNTheme ? '#355ffe' : COLORS.brandSecondary)};
    animation: ${({ isCNTheme }) => (isCNTheme ? cnAnimation : tiAnimation)} 2s linear infinite forwards;
  }
`;

interface TrueImpactLoadingProps {
  className?: string;
  singleLine?: boolean;
  marginTop?: number;
  marginBottom?: number;
  paddingTop?: number;
  paddingBottom?: number;
}

const TrueImpactLoading: React.FC<TrueImpactLoadingProps> = ({
  className,
  singleLine,
  marginTop = 30,
  marginBottom = 30,
  paddingTop,
  paddingBottom,
}) => {
  // Check localStorage directly - it's available immediately
  const isBbTheme = isBbUserView();
  const isCNTheme = isCnUserView() && !isBbTheme;
  const isVisible = true;

  if (isBbTheme) {
    return <BbSpinner marginTop={marginTop} marginBottom={marginBottom} className={className} />;
  }

  return (
    <StyledDiv
      className={className}
      marginTop={marginTop}
      marginBottom={marginBottom}
      paddingTop={paddingTop}
      paddingBottom={paddingBottom}
      isCNTheme={isCNTheme}
      isVisible={isVisible}
    >
      <div className="dot" />
      <div className="dot" />
      <div className="dot" />
      {!singleLine && (
        <>
          <div className="dot" />
          <div className="dot" />
          <div className="dot" />
        </>
      )}
    </StyledDiv>
  );
};

export default TrueImpactLoading;
